import { useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { CalendarPlus, ExternalLink, Globe, Radio } from 'lucide-react';
import { clusterApi, sitesApi, sourcesApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';
import { hasJbsConsole, siteAirChainUrl, siteProductionUrl, siteWebHost, siteWebUrl } from '@/lib/siteUrls';
import { productionAnchor } from '@/hooks/useProductionIndex';
import { useActingOrg } from '@/hooks/useActingOrg';
import { eventFeedHref } from '@/lib/eventFeedPrefill';
import type { Production as ProductionT, ProductionAirChain, Site, Source } from '@/types';

/**
 * Production surface, step 1 of JSS#1161: a read-only index of each JBS site's
 * productions (JBS LiveEvents) and their AirChains, read live from the site
 * through deployment-service (`GET /api/cluster/:siteId/productions`). Running
 * a production still happens in the site's JBS console, which every row links
 * into. Each site is fetched on its own, so one unreachable site does not
 * blank the others.
 */
/** A resolvable source: its name, and the org an event fed from it belongs to. */
// `retired` (JSS#1151): the relay refuses publishes to a retired source, so
// it is not offered as an event feed (as FeedPicker does not offer it). The
// retirer reinstates a source an AirChain references again on its next pass.
type SourceRefs = Map<string, { name: string; orgId: string | undefined; retired: boolean }>;

export default function Production() {
  const { data: sites, isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
  });
  // Only to name the JSS source a cloud output publishes to. A failure here
  // just leaves the source id shown.
  const { data: sources } = useQuery({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
  });
  // "Use as event feed" (JSS#1182) names the source's org as the event's
  // owner, else the org the console acts in.
  const { actingOrgId } = useActingOrg();
  const sourceNames: SourceRefs = new Map(
    (sources ?? []).map((s: Source) => [
      s.id,
      { name: s.name, orgId: s.orgId ?? actingOrgId, retired: s.status === 'retired' },
    ]),
  );
  const jbsSites = (sites ?? []).filter(hasJbsConsole);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-bold text-silver-bright">Production</h2>
        <p className="text-sm text-silver-muted mt-1">
          The productions and AirChains on each of your JBS sites. To run a production, open it in
          the site's JBS console.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-silver-muted">Loading sites...</p>
      ) : error ? (
        <p className="text-sm text-status-error">Failed to load sites</p>
      ) : jbsSites.length === 0 ? (
        <div className="bg-surface border border-surface-elevated rounded-xl">
          <p className="p-5 text-sm text-silver-muted">
            No site has JBS installed yet. Install JBS on a site from{' '}
            <Link to="/sites" className="text-gold hover:underline">
              Sites
            </Link>
            .
          </p>
        </div>
      ) : (
        jbsSites.map((site) => <SiteProductionsSection key={site.id} site={site} sourceNames={sourceNames} />)
      )}
    </div>
  );
}

function SiteProductionsSection({ site, sourceNames }: { site: Site; sourceNames: SourceRefs }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['site-productions', site.id],
    queryFn: () => clusterApi.getProductions(site.id),
    refetchInterval: 30_000,
    retry: false,
  });

  let body: ReactNode;
  if (isLoading) {
    body = <p className="p-4 text-sm text-silver-muted">Loading productions...</p>;
  } else if (error) {
    const parsed = parseApiError(error);
    body = (
      <p className="p-4 text-sm text-status-error" role="alert">
        {parsed.status === 503
          ? "Can't reach this site's cluster right now."
          : "Couldn't read this site's productions."}
      </p>
    );
  } else if (!data || !data.installed) {
    body = <p className="p-4 text-sm text-silver-muted">JBS isn't running on this site's cluster.</p>;
  } else if (data.productions.length === 0 && data.unassignedAirChains.length === 0) {
    body = <p className="p-4 text-sm text-silver-muted">No productions on this site yet.</p>;
  } else {
    body = (
      <div className="p-4 space-y-4">
        {data.productions.map((p) => (
          <ProductionCard key={`${p.namespace}/${p.name}`} site={site} production={p} sourceNames={sourceNames} />
        ))}
        {data.unassignedAirChains.length > 0 && (
          <div className="border border-surface-elevated rounded-lg">
            <div className="px-4 py-3 border-b border-surface-elevated">
              <h4 className="text-sm font-medium text-silver-bright">Not in a production</h4>
              <p className="text-xs text-silver-muted">
                AirChains bound to no production. The site console only shows a chain inside its
                production.
              </p>
            </div>
            <AirChainList site={site} chains={data.unassignedAirChains} sourceNames={sourceNames} />
          </div>
        )}
      </div>
    );
  }

  const anchor = productionAnchor(site.id);
  const ref = useScrollToHash<HTMLElement>(anchor);

  return (
    <section ref={ref} id={anchor} className="bg-surface border border-surface-elevated rounded-xl scroll-mt-4" aria-label={site.name}>
      <div className="flex items-center justify-between gap-4 p-4 border-b border-surface-elevated">
        <div className="min-w-0">
          <Link to={`/sites/${site.id}`} className="text-base font-semibold text-silver-bright hover:text-gold">
            {site.name}
          </Link>
          {site.location && <p className="text-xs text-silver-muted truncate">{site.location}</p>}
        </div>
        <a
          href={siteWebUrl(site)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors shrink-0"
          title={siteWebHost(site)}
        >
          <Globe size={12} />
          Open site console
          <ExternalLink size={10} />
        </a>
      </div>
      {body}
    </section>
  );
}

/**
 * Scrolls an element into view once when the URL hash names it. Sources and
 * the feed picker link to `/production#production-<siteId>-<name>` (or
 * `#site-<siteId>`); the target mounts only after its site's read lands, so
 * the browser's own hash scroll would have run too early.
 */
function useScrollToHash<T extends HTMLElement>(id: string) {
  const { hash } = useLocation();
  const ref = useRef<T>(null);
  useEffect(() => {
    if (hash === `#${id}`) ref.current?.scrollIntoView?.({ block: 'start' });
  }, [hash, id]);
  return ref;
}

const PHASE_STYLE: Record<string, string> = {
  Active: 'bg-status-healthy/20 text-status-healthy',
  Degraded: 'bg-status-warning/20 text-status-warning',
  Error: 'bg-status-error/20 text-status-error',
};

function PhaseBadge({ phase }: { phase?: string }) {
  const style = (phase && PHASE_STYLE[phase]) || 'bg-surface-elevated text-silver-muted';
  return <span className={`px-2 py-0.5 text-xs font-medium rounded ${style}`}>{phase ?? 'No status'}</span>;
}

function ProductionCard({
  site, production, sourceNames,
}: { site: Site; production: ProductionT; sourceNames: SourceRefs }) {
  const anchor = productionAnchor(site.id, production.name);
  const ref = useScrollToHash<HTMLElement>(anchor);
  return (
    <article ref={ref} id={anchor} className="border border-surface-elevated rounded-lg scroll-mt-4" aria-label={production.label}>
      <div className="px-4 py-3 border-b border-surface-elevated">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className="text-sm font-medium text-silver-bright truncate">{production.label}</h4>
            <PhaseBadge phase={production.phase} />
          </div>
          <a
            href={siteProductionUrl(site, production.name)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-gold hover:text-gold-light shrink-0"
            aria-label={`Open ${production.label} in the site console`}
          >
            Open <ExternalLink size={10} />
          </a>
        </div>
        {production.description && <p className="text-xs text-silver-muted mt-1">{production.description}</p>}
        {production.message && <p className="text-xs text-status-warning mt-1">{production.message}</p>}
      </div>
      {production.airChains.length === 0 ? (
        <p className="px-4 py-3 text-xs text-silver-muted">No AirChains in this production.</p>
      ) : (
        <AirChainList site={site} liveEventName={production.name} chains={production.airChains} sourceNames={sourceNames} />
      )}
    </article>
  );
}

function AirChainList({
  site, liveEventName, chains, sourceNames,
}: { site: Site; liveEventName?: string; chains: ProductionAirChain[]; sourceNames: SourceRefs }) {
  return (
    <ul className="divide-y divide-surface-elevated">
      {chains.map((ac) => {
        const cloud = ac.outputs.filter((o) => o.destination === 'jss-cloud');
        const title = ac.label ?? ac.name;
        return (
          <li key={`${ac.namespace}/${ac.name}`} className="px-4 py-3" data-testid={`airchain-${ac.name}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                {liveEventName ? (
                  <a
                    href={siteAirChainUrl(site, liveEventName, ac.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-silver-bright hover:text-gold"
                  >
                    {title}
                  </a>
                ) : (
                  <span className="text-sm text-silver-bright">{title}</span>
                )}
                {ac.label && <span className="ml-2 text-xs text-silver-muted font-mono">{ac.name}</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0 text-xs">
                {ac.onAir && (
                  <span className="flex items-center gap-1 font-semibold text-status-error">
                    <Radio size={12} /> On air
                  </span>
                )}
                {ac.nodesTotal !== undefined && (
                  <span className="text-silver-muted">
                    {ac.nodesReady ?? 0}/{ac.nodesTotal} nodes ready
                  </span>
                )}
                <PhaseBadge phase={ac.phase} />
              </div>
            </div>
            {cloud.length > 0 && (
              <ul className="mt-2 space-y-1">
                {cloud.map((o) => {
                  const ref = o.jssSourceId ? sourceNames.get(o.jssSourceId) : undefined;
                  return (
                    <li key={o.name} className="text-xs text-silver-muted">
                      <span className="font-mono">{o.name}</span>
                      {' → '}
                      {o.jssSourceId ? (
                        ref ? (
                          <>
                            <Link to={`/streaming/sources/${o.jssSourceId}`} className="text-gold hover:underline">
                              {ref.name}
                            </Link>
                            {ref.retired ? (
                              <span className="ml-3 text-silver-muted/70">retired</span>
                            ) : (
                              <Link
                                to={eventFeedHref(o.jssSourceId, ref.orgId)}
                                className="ml-3 inline-flex items-center gap-1 text-gold hover:underline"
                                aria-label={`Use ${o.name} as event feed`}
                              >
                                <CalendarPlus size={11} /> Use as event feed
                              </Link>
                            )}
                          </>
                        ) : (
                          <span className="font-mono" title="Not one of this organization's sources">
                            source {o.jssSourceId}
                          </span>
                        )
                      ) : (
                        <span>JSS cloud{o.type === 'rift_egress' ? ' (Rift)' : ''}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
